// SVGAttributes (not SVGProps): SVGProps folds in ClassAttributes' `ref`,
// whose type differs across the duplicate @types/react copies (shared's vs
// each app's) and breaks the consumers' typechecks. The shim never forwards
// refs, so attribute-only typing is both sufficient and copy-agnostic.
import type { SVGAttributes } from 'react';

/**
 * Cross-platform SVG primitives — web shim.
 *
 * Mirrors the `react-native-svg` API so that a single icon source file written
 * against these primitives renders on both web and native without changes.
 *
 * Web side: thin uppercase-named wrappers around the standard lowercase SVG
 * intrinsic elements (`<svg>`, `<path>`, etc.). Native side
 * (`./index.native.tsx`) re-exports from `react-native-svg`.
 *
 * SVGAttributes (not SVGProps): `ref` is nominally incompatible between the
 * monorepo's two @types/react copies (shared/node_modules vs each app's) —
 * same cast-avoidance as TipArrow.tsx and Pressable.tsx.
 *
 * Example custom icon (one source file, both platforms):
 *
 * ```tsx
 * import { Svg, Path } from '@flashka/shared/ui/svg';
 *
 * export function Heart({ color = 'currentColor', size = 24 }) {
 *   return (
 *     <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
 *       <Path d="..." stroke={color} strokeWidth={2} />
 *     </Svg>
 *   );
 * }
 * ```
 */
// `flexShrink: 0` by default: an svg in a width-constrained flex row (a Badge
// chip, a Button) must never be squashed narrower than its box — text absorbs
// the squeeze, graphics don't. Callers can still override via `style`.
// Harmless for the width:"100%" art svgs (they never overflow their box).
export const Svg = ({ style, ...props }: SVGAttributes<SVGSVGElement>) => (
  <svg style={{ flexShrink: 0, ...style }} {...props} />
);
export const Path = (props: SVGAttributes<SVGPathElement>) => <path {...props} />;
export const Circle = (props: SVGAttributes<SVGCircleElement>) => <circle {...props} />;
export const Rect = (props: SVGAttributes<SVGRectElement>) => <rect {...props} />;
export const Ellipse = (props: SVGAttributes<SVGEllipseElement>) => (
  <ellipse {...props} />
);
export const Line = (props: SVGAttributes<SVGLineElement>) => <line {...props} />;
export const Polygon = (props: SVGAttributes<SVGPolygonElement>) => (
  <polygon {...props} />
);
export const Polyline = (props: SVGAttributes<SVGPolylineElement>) => (
  <polyline {...props} />
);
export const G = (props: SVGAttributes<SVGGElement>) => <g {...props} />;
export const Defs = (props: SVGAttributes<SVGDefsElement>) => <defs {...props} />;
export const LinearGradient = (props: SVGAttributes<SVGLinearGradientElement>) => (
  <linearGradient {...props} />
);
export const RadialGradient = (props: SVGAttributes<SVGRadialGradientElement>) => (
  <radialGradient {...props} />
);
export const Stop = (props: SVGAttributes<SVGStopElement>) => <stop {...props} />;
export const Mask = (props: SVGAttributes<SVGMaskElement>) => <mask {...props} />;
export const ClipPath = (props: SVGAttributes<SVGClipPathElement>) => (
  <clipPath {...props} />
);
export const Use = (props: SVGAttributes<SVGUseElement>) => <use {...props} />;
export const Symbol = (props: SVGAttributes<SVGSymbolElement>) => <symbol {...props} />;
export const Pattern = (props: SVGAttributes<SVGPatternElement>) => (
  <pattern {...props} />
);
export const TextSvg = (props: SVGAttributes<SVGTextElement>) => <text {...props} />;
export const TSpan = (props: SVGAttributes<SVGTSpanElement>) => <tspan {...props} />;
